'use client'

import { LineChevronRightSmall } from '@qr-front/icons/line-chevron-right-small'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { sectionsListAtom } from 'features/generator/contentFormSections/MenuForm/MenuForm'
import {
	restaurantDescriptionAtom,
	restaurantImageAtom,
	restaurantImageFileAtom,
	restaurantNameAtom,
} from 'features/generator/contentFormSections/MenuForm/RestaurantInfo'
import { QrScreen } from 'features/generator/QrScreen'
import { TQrScreenProps } from 'features/generator/QrScreen/QrScreen'
import { formatMenuListWithDefaultSectionNames } from 'features/generator/utils/menuListFormat'
import { useAtom, useAtomValue } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { TUploadedMedia } from 'services/mediaStorage'

import {
	primaryColorAtom,
	secondaryColorAtom,
} from '@/content-from-sections/client'

import { IsPreviewModeProvider } from './MenuScreenContexts'
import { MenuTabView } from './MenuTabView'
import { RestaurantLogo } from './restaurantLogo'

const DEFAULT_MENU_SECTION_LIST = [
	{
		id: 'starters',
		name: 'generator__qr_view__menu_default_section_starters',
	},
	{
		id: 'soups-salad',
		name: 'generator__qr_view__menu_default_section_soups_salad',
	},
	{
		id: 'pizza',
		name: 'generator__qr_view__menu_default_section_pizza',
	},
	{
		id: 'desserts',
		name: 'generator__qr_view__menu_default_section_desserts',
	},
	{
		id: 'drinks',
		name: 'generator__qr_view__menu_default_section_drinks',
	},
]

export const MENU_DEMO_COLOR = getVersionForClone({
	base: '#666363',
	clone1: '#506F6C',
})

type TMenuScreenProps = {
	previewMode: boolean
	demoMode: boolean
}

export const activeSectionIdAtom = atomWithReset<number | null>(null)

export const Image = ({
	image,
	localSrc,
	demoMode,
	className,
}: {
	image?: TUploadedMedia | null
	localSrc?: string | null
	demoMode: boolean
	className?: string
}) => {
	const imageSrc = image?.publicId
		? `${image.bucketRootUrl}/${image.publicId}`
		: localSrc

	if (imageSrc) {
		return (
			// eslint-disable-next-line @next/next/no-img-element
			<img
				alt={image?.publicId ?? 'uploading'}
				src={imageSrc}
				className={cn(
					'aspect-[120/68] w-30 rounded-4 object-contain',
					className,
				)}
			/>
		)
	}

	if (demoMode) {
		return <RestaurantLogo />
	}

	return null
}

const Header = ({ demoMode }: { demoMode: boolean }) => {
	const t = useTranslations()

	const restaurantName = useAtomValue(restaurantNameAtom)
	const restaurantDescription = useAtomValue(restaurantDescriptionAtom)
	const restaurantImage = useAtomValue(restaurantImageAtom)
	const restaurantImageFile = useAtomValue(restaurantImageFileAtom)
	const secondaryColor = useAtomValue(secondaryColorAtom)

	const [localPreviewSrc, setLocalPreviewSrc] = useState<string | null>(null)
	useEffect(() => {
		if (!restaurantImageFile) {
			setLocalPreviewSrc(null)
			return
		}
		const url = URL.createObjectURL(restaurantImageFile)
		setLocalPreviewSrc(url)
		return () => URL.revokeObjectURL(url)
	}, [restaurantImageFile])

	return (
		<div className="z-10 mb-5 flex max-w-[220px] flex-col items-center gap-6">
			<div
				className="flex w-full flex-col items-center gap-4 text-white"
				style={{ color: secondaryColor }}
			>
				<Image
					image={restaurantImage}
					localSrc={localPreviewSrc}
					demoMode={demoMode}
				/>
				<div className="w-full">
					<h4 className="type-heading-4 max-w-full overflow-hidden break-words text-center">
						{!demoMode
							? restaurantName
							: t('generator__qr_view__menu_default_name')}
					</h4>
					<p className="break-words break-keep text-center text-10">
						{!demoMode
							? restaurantDescription
							: t('generator__qr_view__menu_default_description')}
					</p>
				</div>
			</div>
		</div>
	)
}

export const Content = ({ previewMode, demoMode }: TMenuScreenProps) => {
	const t = useTranslations()

	const [initialSectionId, setInitialSectionId] = useAtom(activeSectionIdAtom)
	const menuSectionList = useAtomValue(sectionsListAtom)

	const primaryColor = useAtomValue(primaryColorAtom)
	const secondaryColor = useAtomValue(secondaryColorAtom)

	const filteredMenuSectionList = menuSectionList.filter(
		(section) => section.visible,
	)

	const menuList = demoMode
		? DEFAULT_MENU_SECTION_LIST
		: formatMenuListWithDefaultSectionNames(filteredMenuSectionList, t)

	const backgroundColor = demoMode ? MENU_DEMO_COLOR : primaryColor

	return (
		<IsPreviewModeProvider value={previewMode}>
			{initialSectionId !== null && !demoMode ? (
				<MenuTabView demoMode={demoMode} previewMode={previewMode} />
			) : (
				<div className={cn('relative', demoMode && 'pointer-events-none')}>
					<div
						className="-mt-1 w-full pb-6 pt-7 transition-colors"
						style={{ backgroundColor }}
					>
						<div className="relative grid place-items-center gap-6">
							<Header demoMode={demoMode} />
						</div>
					</div>
					<div className="mx-auto -mt-6 flex w-full max-w-qr-screen-constrained flex-col gap-2 px-5">
						{menuList.map((section, index) => (
							<div
								key={index}
								className={cn(
									'flex h-12 items-center justify-between gap-2 rounded-6 border-b-2 border-solid bg-white p-2 shadow-generator',
									!demoMode && index === menuList.length - 1 && 'mb-5',
									getVersionForClone({
										base: 'border-b-yellow',
										clone1: 'border-b-[#94BCB8]',
									}),
								)}
								style={{
									borderColor: !demoMode ? secondaryColor : undefined,
								}}
								onClick={() => setInitialSectionId(index)}
							>
								<span className="type-body-small w-full overflow-hidden truncate text-ellipsis font-semibold">
									{demoMode ? t(section.name) : section.name}
								</span>
								<LineChevronRightSmall className="text-grey" />
							</div>
						))}
					</div>
				</div>
			)}
		</IsPreviewModeProvider>
	)
}

export const MenuScreen = (props: TQrScreenProps) => {
	const primaryColor = useAtomValue(primaryColorAtom)

	return (
		<QrScreen
			{...props}
			primaryColor={props.demoMode ? MENU_DEMO_COLOR : primaryColor}
			content={<Content {...props} />}
		/>
	)
}
